/**
 * Copyright (c) 2026 Lauft App Inc. All rights reserved.
 *
 * This source code is proprietary and confidential.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 */

/**
 * StudioCampaigns — Lauft Studio cold-outbound campaign builder.
 *
 * Replaces running buildStudioCampaign.js from a terminal. Same service
 * underneath (services/studioCampaignService.js), so this and the CLI can
 * never disagree about who gets suppressed.
 *
 * Lives in the ADMIN DASHBOARD, not the Studio portal. The Studio portal is a
 * fully public surface with no login anywhere in it; this screen mints live
 * free-video tokens and exports agent PII, so it belongs behind auth that
 * already exists and is already audited. Being inside AdminLayout is the whole
 * of its access control — there is no permission logic in this file.
 *
 * Flow: preview (writes nothing) → read the numbers → build → download CSV →
 * upload to Instantly.
 */
import React, { useState, useEffect, useCallback } from 'react';
import AuthService from '../../services/authService';

const API_URL = 'https://back-end-x7tx.onrender.com/api';

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return (
    dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
    ', ' +
    dt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  );
};

const pct = (n, d) => (d ? Math.round((n / d) * 100) : 0);

// Coverage bands. Below ~40% a market isn't really a channel — worth seeing at
// a glance rather than doing the arithmetic per row.
const coverageClass = (p) =>
  p >= 70 ? 'pill-complete' : p >= 40 ? 'pill-processing' : 'pill-failed';

const StudioCampaigns = () => {
  // Build form
  const [days, setDays] = useState(7);
  const [campaignName, setCampaignName] = useState('');
  const [limit, setLimit] = useState('');

  // Live links from the last build. Real single-use tokens — the preview
  // endpoint's sample rows have tokens that were never persisted and 404.
  const [links, setLinks] = useState([]);
  const [copiedIdx, setCopiedIdx] = useState(null);

  const [preview, setPreview] = useState(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isBuilding, setIsBuilding] = useState(false);
  const [confirmBuild, setConfirmBuild] = useState(false);

  const [campaigns, setCampaigns] = useState([]);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);

  // Drill-down
  const [openCampaign, setOpenCampaign] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [isLoadingProspects, setIsLoadingProspects] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // ── Data ────────────────────────────────────────────────────────────

  const request = async (path, method = 'GET', body = null) => {
    const config = { method, headers: {} };
    if (body) config.body = JSON.stringify(body);
    const res = await AuthService.fetchWithAuth(`${API_URL}${path}`, config);
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.error || json.message || `Request failed (${res.status})`);
    return json;
  };

  const fetchCampaigns = useCallback(async () => {
    setIsLoadingCampaigns(true);
    try {
      const json = await request('/studio/admin/campaigns');
      setCampaigns(json.data || []);
    } catch (err) {
      setError('Error loading campaigns: ' + err.message);
    } finally {
      setIsLoadingCampaigns(false);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const handlePreview = async () => {
    setIsPreviewing(true);
    setError('');
    setSuccess('');
    setConfirmBuild(false);
    try {
      const json = await request('/studio/admin/campaigns/preview', 'POST', {
        days: Number(days),
        limit: limit ? Number(limit) : undefined,
      });
      setPreview(json.data);
    } catch (err) {
      setError('Preview failed: ' + err.message);
      setPreview(null);
      setLinks([]);
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleBuild = async () => {
    setIsBuilding(true);
    setError('');
    try {
      const json = await request('/studio/admin/campaigns', 'POST', {
        days: Number(days),
        campaign: campaignName.trim() || undefined,
        limit: limit ? Number(limit) : undefined,
      });

      // CSV arrives as a string in the JSON body, not as a file response —
      // a real download response would need the admin JWT in a query string
      // to work from an <a download>, which puts it in browser history.
      const blob = new Blob([json.data.csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = json.data.filename || 'studio-campaign.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setSuccess(
        `Built "${json.data.campaign}" — ${json.data.created} prospects. CSV downloaded.`
      );
      setLinks(json.data.links || []);
      setPreview(null);
      setConfirmBuild(false);
      setCampaignName('');
      fetchCampaigns();
    } catch (err) {
      setError('Build failed: ' + err.message);
    } finally {
      setIsBuilding(false);
    }
  };

  const openProspects = async (name) => {
    if (openCampaign === name) {
      setOpenCampaign(null);
      setProspects([]);
      return;
    }
    setOpenCampaign(name);
    setIsLoadingProspects(true);
    try {
      const json = await request(`/studio/admin/campaigns/${encodeURIComponent(name)}/prospects?limit=100`);
      setProspects(json.data?.prospects || []);
    } catch (err) {
      setError('Error loading prospects: ' + err.message);
      setProspects([]);
    } finally {
      setIsLoadingProspects(false);
    }
  };

  const boardRows = preview
    ? Object.entries(preview.byBoard || {}).sort((a, b) => b[1].total - a[1].total)
    : [];

  return (
    <div className="admin-panel" style={{ color: '#111', padding: '32px 0 0' }}>
      <style>{`
        /* Studio Campaigns — Control Center design system (matches VideoJobsManager) */

        .admin-panel .dashboard-card {
          background: #fff; border-radius: 14px; padding: 20px;
          border: 1px solid #f0f0f0; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
          margin-bottom: 20px;
        }
        .admin-panel .card-header {
          display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px;
        }
        .admin-panel .card-header h3 {
          margin: 0; font-size: 11px; font-weight: 600; color: #9ca3af;
          letter-spacing: 0.06em; text-transform: uppercase;
        }

        .admin-panel .alert-success {
          background-color: rgba(34, 197, 94, 0.08); color: #166534;
          border: 1px solid rgba(34, 197, 94, 0.25); padding: 11px 14px;
          border-radius: 10px; margin-bottom: 16px; font-size: 13px; font-weight: 500;
        }
        .admin-panel .alert-danger {
          background-color: rgba(220, 38, 38, 0.08); color: #991b1b;
          border: 1px solid rgba(220, 38, 38, 0.25); padding: 11px 14px;
          border-radius: 10px; margin-bottom: 16px; font-size: 13px; font-weight: 500;
        }
        .admin-panel .alert-warn {
          background-color: rgba(245, 158, 11, 0.09); color: #92600a;
          border: 1px solid rgba(245, 158, 11, 0.28); padding: 11px 14px;
          border-radius: 10px; margin-bottom: 16px; font-size: 13px; font-weight: 500;
        }

        .admin-panel .form-row { display: flex; gap: 14px; align-items: flex-end; flex-wrap: wrap; }
        .admin-panel .form-field { display: flex; flex-direction: column; gap: 6px; }
        .admin-panel .form-field label {
          font-size: 10px; font-weight: 700; color: #9ca3af;
          text-transform: uppercase; letter-spacing: 0.08em;
        }
        .admin-panel .form-field input {
          border: 1px solid #e5e7eb; border-radius: 9px; padding: 9px 12px;
          font-size: 13px; color: #111; font-family: inherit; outline: none; background: #fff;
        }
        .admin-panel .form-field input:focus { border-color: #1e3a8a; }

        .admin-panel .btn {
          padding: 9px 16px; border-radius: 8px; font-size: 12px; font-weight: 600;
          cursor: pointer; font-family: inherit; border: 1px solid transparent;
          transition: all 0.15s; white-space: nowrap;
        }
        .admin-panel .btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .admin-panel .btn-primary { background: #1e3a8a; color: #fff; }
        .admin-panel .btn-primary:hover:not(:disabled) { background: #1c357d; }
        .admin-panel .btn-danger { background: #b91c1c; color: #fff; }
        .admin-panel .btn-danger:hover:not(:disabled) { background: #991b1b; }
        .admin-panel .btn-secondary { background: #fff; color: #6b7280; border-color: #e5e7eb; }
        .admin-panel .btn-secondary:hover:not(:disabled) { background: #fafafa; color: #111; }

        .admin-panel .stat-grid {
          display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 10px;
        }
        .admin-panel .stat-box {
          border: 1px solid #f0f0f0; border-radius: 10px; padding: 12px 14px; background: #fafafa;
        }
        .admin-panel .stat-box .stat-label {
          font-size: 10px; font-weight: 700; color: #9ca3af;
          text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 4px;
        }
        .admin-panel .stat-box .stat-value { font-size: 20px; font-weight: 700; color: #111; }
        .admin-panel .stat-box.headline { background: rgba(30, 58, 138, 0.05); border-color: rgba(30, 58, 138, 0.2); }
        .admin-panel .stat-box.headline .stat-value { color: #1e3a8a; }

        .admin-panel .table-container { margin: 0 -20px -20px; overflow-x: auto; }
        .admin-panel .data-table {
          width: 100%; border-collapse: collapse; background: #fff;
          border: none; border-top: 1px solid #f0f0f0;
        }
        .admin-panel .data-table thead th {
          background: #fafafa; padding: 11px 14px; text-align: left; font-weight: 700;
          font-size: 10px; color: #9ca3af; text-transform: uppercase;
          letter-spacing: 0.08em; border-bottom: 1px solid #f0f0f0;
        }
        .admin-panel .data-table td {
          padding: 12px 14px; border-bottom: 1px solid #f5f5f5; color: #111;
          font-size: 13px; text-align: left; vertical-align: middle;
        }
        .admin-panel .data-table tr:last-child td { border-bottom: none; }
        .admin-panel .data-table tbody tr:hover { background: #fafafa; }
        .admin-panel .data-table tbody tr.clickable { cursor: pointer; }

        .admin-panel .pill {
          display: inline-block; padding: 2px 9px; border-radius: 999px; font-size: 10px;
          font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; white-space: nowrap;
        }
        .admin-panel .pill-processing { background: rgba(245, 158, 11, 0.14); color: #b45309; }
        .admin-panel .pill-complete   { background: rgba(16, 185, 129, 0.12); color: #047857; }
        .admin-panel .pill-failed     { background: rgba(220, 38, 38, 0.1);  color: #b91c1c; }

        .admin-panel .listing-address { font-weight: 500; color: #111; }
        .admin-panel .listing-csz { font-size: 11px; color: #6b7280; }
        .admin-panel .user-name { font-size: 13px; font-weight: 600; color: #111; }
        .admin-panel .user-email { font-size: 11px; color: #6b7280; }
        .admin-panel .muted { color: #9ca3af; }
        .admin-panel .empty-state { padding: 32px; text-align: center; color: #9ca3af; font-size: 13px; }
      `}</style>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
        <img src="/lauft-logo.png" alt="Lauft" style={{ width: '22px', height: '22px', objectFit: 'contain' }} />
        <span style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.08em', color: '#888', textTransform: 'uppercase' }}>
          Studio Campaigns
        </span>
      </div>

      {error && <div className="alert-danger">{error}</div>}
      {success && <div className="alert-success">{success}</div>}

      {/* ── Build ─────────────────────────────────────────────────── */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3>Build a campaign</h3>
        </div>

        <div className="form-row">
          <div className="form-field">
            <label>Lookback (days)</label>
            <input
              type="number"
              min="1"
              max="90"
              value={days}
              onChange={(e) => { setDays(e.target.value); setPreview(null); setConfirmBuild(false); }}
              style={{ width: '110px' }}
            />
          </div>
          <div className="form-field">
            <label>Limit (blank = all)</label>
            <input
              type="number"
              min="1"
              placeholder="all"
              value={limit}
              onChange={(e) => { setLimit(e.target.value); setPreview(null); setConfirmBuild(false); }}
              style={{ width: '110px' }}
            />
          </div>
          <div className="form-field" style={{ flex: 1, minWidth: '220px' }}>
            <label>Campaign name (optional)</label>
            <input
              type="text"
              placeholder={`studio-${new Date().toISOString().slice(0, 10)}`}
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
            />
          </div>
          <button className="btn btn-secondary" onClick={handlePreview} disabled={isPreviewing || isBuilding}>
            {isPreviewing ? 'Checking…' : 'Preview'}
          </button>
        </div>

        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '10px' }}>
          Reads listings synced in the last {days} day{Number(days) === 1 ? '' : 's'}. Preview writes nothing.
          {limit && ` Capped at ${limit} prospect${Number(limit) === 1 ? '' : 's'} — use a small number for test links.`}
        </div>
      </div>

      {/* ── Preview results ───────────────────────────────────────── */}
      {preview && (
        <div className="dashboard-card">
          <div className="card-header">
            <h3>Preview — nothing written</h3>
          </div>

          <div className="stat-grid" style={{ marginBottom: '16px' }}>
            <div className="stat-box headline">
              <div className="stat-label">Prospects</div>
              <div className="stat-value">{preview.created}</div>
            </div>
            <div className="stat-box">
              <div className="stat-label">Candidates</div>
              <div className="stat-value">{preview.candidates}</div>
            </div>
            <div className="stat-box">
              <div className="stat-label">No agent email</div>
              <div className="stat-value">{preview.noAgentEmail}</div>
            </div>
            <div className="stat-box">
              <div className="stat-label">Too few photos</div>
              <div className="stat-value">{preview.tooFewPhotos}</div>
            </div>
            <div className="stat-box">
              <div className="stat-label">Same agent collapsed</div>
              <div className="stat-value">{preview.dupeInBatch}</div>
            </div>
            <div className="stat-box">
              <div className="stat-label">Already Lauft agents</div>
              <div className="stat-value">{preview.existingAgent}</div>
            </div>
            <div className="stat-box">
              <div className="stat-label">Already prospected</div>
              <div className="stat-value">{preview.alreadyProspected}</div>
            </div>
          </div>

          {/* The single most useful number here: which markets this channel
              actually works in. Coverage is board-dependent — some don't
              syndicate the listing agent at all. */}
          <div style={{ fontSize: '10px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>
            Agent-email coverage by board
          </div>
          <table className="data-table" style={{ borderTop: '1px solid #f0f0f0', marginBottom: '18px' }}>
            <thead>
              <tr><th>Board</th><th>With email</th><th>Total</th><th>Coverage</th></tr>
            </thead>
            <tbody>
              {boardRows.map(([board, c]) => {
                const p = pct(c.withEmail, c.total);
                return (
                  <tr key={board}>
                    <td style={{ fontWeight: 600 }}>{board}</td>
                    <td>{c.withEmail}</td>
                    <td className="muted">{c.total}</td>
                    <td><span className={`pill ${coverageClass(p)}`}>{p}%</span></td>
                  </tr>
                );
              })}
              {boardRows.length === 0 && (
                <tr><td colSpan="4" className="empty-state">No listings in this window.</td></tr>
              )}
            </tbody>
          </table>

          {preview.created === 0 ? (
            <div className="alert-warn">
              Nothing to build — no prospects matched. Try a wider lookback window.
            </div>
          ) : !confirmBuild ? (
            <button className="btn btn-primary" onClick={() => setConfirmBuild(true)}>
              Build campaign ({preview.created} prospects)
            </button>
          ) : (
            <div>
              {/* Deliberate second step. Building mints one live, single-use
                  free-video token per prospect — each redeemable for a real
                  ~$4-5 render — and permanently marks those agents as
                  prospected, so they're excluded from every future campaign.
                  Not an undo-able action. */}
              <div className="alert-warn">
                This creates {preview.created} prospects with live free-video tokens and marks
                those agents as contacted. They won't appear in future campaigns.
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="btn btn-danger" onClick={handleBuild} disabled={isBuilding}>
                  {isBuilding ? 'Building…' : 'Confirm & download CSV'}
                </button>
                <button className="btn btn-secondary" onClick={() => setConfirmBuild(false)} disabled={isBuilding}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {preview.sample?.length > 0 && (
            <div style={{ marginTop: '18px' }}>
              <div style={{ fontSize: '10px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>
                Sample rows
              </div>
              <table className="data-table" style={{ borderTop: '1px solid #f0f0f0' }}>
                <thead><tr><th>Agent</th><th>Brokerage</th><th>Listing</th></tr></thead>
                <tbody>
                  {preview.sample.map((r, i) => (
                    <tr key={i}>
                      <td>
                        <div className="user-name">{r.full_name || <span className="muted">—</span>}</div>
                        <div className="user-email">{r.email}</div>
                      </td>
                      <td className="muted">{r.brokerage || '—'}</td>
                      <td>
                        <div className="listing-address">{r.listing_address}</div>
                        <div className="listing-csz">{r.listing_csz}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Live links from the last build ────────────────────────── */}
      {links.length > 0 && (
        <div className="dashboard-card">
          <div className="card-header">
            <h3>Test links — live</h3>
            <button className="btn btn-secondary" onClick={() => setLinks([])}>Dismiss</button>
          </div>

          {/* One-shot generation per token. Worth saying out loud: someone
              testing the generate path will burn a link on the first try and
              wonder why the second attempt returns the same video. */}
          <div className="alert-warn">
            These are real, working links. Each can generate <strong>one</strong> video — once
            used, that link returns the same video instead of making a new one. Hand out
            several if the generate flow is being tested repeatedly.
          </div>

          <table className="data-table" style={{ borderTop: '1px solid #f0f0f0' }}>
            <thead><tr><th>Agent</th><th>Listing</th><th>Link</th></tr></thead>
            <tbody>
              {links.map((l, i) => (
                <tr key={l.studio_url}>
                  <td>
                    <div className="user-name">{l.full_name || <span className="muted">—</span>}</div>
                    <div className="user-email">{l.email}</div>
                  </td>
                  <td><div className="listing-address">{l.listing_address}</div></td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <a href={l.studio_url} target="_blank" rel="noopener noreferrer"
                         style={{ fontSize: '11px', color: '#1d4ed8', wordBreak: 'break-all' }}>
                        {l.studio_url}
                      </a>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '4px 10px', fontSize: '11px' }}
                        onClick={() => {
                          navigator.clipboard?.writeText(l.studio_url);
                          setCopiedIdx(i);
                          setTimeout(() => setCopiedIdx(null), 1500);
                        }}
                      >
                        {copiedIdx === i ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Past campaigns ────────────────────────────────────────── */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3>Campaigns</h3>
          <button className="btn btn-secondary" onClick={fetchCampaigns} disabled={isLoadingCampaigns}>
            {isLoadingCampaigns ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Campaign</th><th>Built</th><th>Prospects</th>
                <th>Clicked</th><th>Generated</th><th>Signed up</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <React.Fragment key={c.campaign}>
                  <tr className="clickable" onClick={() => openProspects(c.campaign)}>
                    <td style={{ fontWeight: 600 }}>{c.campaign}</td>
                    <td className="muted">{fmtDate(c.createdAt)}</td>
                    <td>{c.prospects}</td>
                    <td>{c.clicked} <span className="muted">({pct(c.clicked, c.prospects)}%)</span></td>
                    <td>{c.generated} <span className="muted">({pct(c.generated, c.prospects)}%)</span></td>
                    <td>{c.signedUp} <span className="muted">({pct(c.signedUp, c.prospects)}%)</span></td>
                  </tr>
                  {openCampaign === c.campaign && (
                    <tr>
                      <td colSpan="6" style={{ background: '#fafafa', padding: '0' }}>
                        {isLoadingProspects ? (
                          <div className="empty-state">Loading prospects…</div>
                        ) : prospects.length === 0 ? (
                          <div className="empty-state">No prospects.</div>
                        ) : (
                          <table className="data-table" style={{ borderTop: 'none' }}>
                            <thead>
                              <tr><th>Agent</th><th>Brokerage</th><th>Listing</th><th>Clicked</th><th>Generated</th></tr>
                            </thead>
                            <tbody>
                              {prospects.map((p) => (
                                <tr key={p._id}>
                                  <td>
                                    <div className="user-name">{p.agentName || <span className="muted">—</span>}</div>
                                    <div className="user-email">{p.agentEmail}</div>
                                  </td>
                                  <td className="muted">{p.brokerage || '—'}</td>
                                  <td>
                                    <div className="listing-address">{p.listing?.address}</div>
                                    <div className="listing-csz">{p.listing?.csz}</div>
                                  </td>
                                  <td className="muted">{fmtDate(p.clickedAt)}</td>
                                  <td className="muted">{fmtDate(p.generatedAt)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {campaigns.length === 0 && !isLoadingCampaigns && (
                <tr><td colSpan="6" className="empty-state">No campaigns yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default StudioCampaigns;